import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { toast } from "sonner";
import { Eye, Ban, Power, Trash2, MoreVertical, CheckCircle2, XCircle, GraduationCap, Pencil, ShieldAlert, ChevronLeft, ChevronRight } from "lucide-react";
import ManageEnrollmentsModal from "@/components/ManageEnrollmentsModal";

const statusColors = {
  approved: "bg-green-100 text-green-800",
  pending: "bg-amber-100 text-amber-800",
  rejected: "bg-red-100 text-red-800",
  active: "bg-blue-100 text-blue-800",
  inactive: "bg-slate-200 text-slate-700",
  deleted: "bg-rose-100 text-rose-800 border border-rose-300",
};

const PAGE_SIZE = 50;

function Pager({ page, pages, total, count, onPage }) {
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600 dark:text-slate-300" data-testid="students-pager">
      <span data-testid="students-pager-summary">
        {total ? `Showing ${from}–${from + count - 1} of ${total.toLocaleString()} students` : "No students"}
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="students-pager-prev">
          <ChevronLeft className="w-4 h-4" /> Prev
        </Button>
        <span className="tabular-nums" data-testid="students-pager-page">Page {page} of {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="students-pager-next">
          Next <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function StudentListPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'deactivate'|'activate'|'delete', student }
  const [coursesList, setCoursesList] = useState([]);
  const [editingCourse, setEditingCourse] = useState(false);
  const [pickedCourseId, setPickedCourseId] = useState("");
  const [manageEnrollmentsFor, setManageEnrollmentsFor] = useState(null); // { id, full_name }

  const canView = hasPerm(user, "student_list", "view");
  const canEdit = hasPerm(user, "student_list", "edit");
  const canDeactivate = hasPerm(user, "student_list", "deactivate");
  const canActivate = hasPerm(user, "student_list", "activate");
  const canDelete = hasPerm(user, "student_list", "delete");
  const isSuper = user.role === "super_admin";

  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [debounced, setDebounced] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [filter, debounced]);
  const reqId = useRef(0);
  const load = () => {
    const id = ++reqId.current;
    return api.get("/users/students", {
      params: { page, page_size: PAGE_SIZE, status: filter, search: debounced || undefined, include_deleted: filter === "deleted" || undefined },
    })
      .then((r) => {
        if (id !== reqId.current) return; // a newer request superseded this one
        if (page > (r.data.pages || 1)) { setPage(r.data.pages || 1); return; }
        setItems(r.data.items || []); setMeta({ total: r.data.total || 0, pages: r.data.pages || 1 });
      })
      .catch(() => {});
  };
  useEffect(() => { load(); }, [filter, debounced, page]);
  useEffect(() => {
    if (isSuper) api.get("/courses").then((r) => setCoursesList(r.data || []));
  }, [isSuper]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/users/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openView = async (s) => {
    setViewing({ student: s, progress: null, recent_activity: null, session: null, security_logs: [] });
    setViewLoading(true);
    setEditingCourse(false);
    setPickedCourseId(s.selected_course_id || "");
    try {
      const [r, sess, logs] = await Promise.allSettled([
        api.get(`/users/students/${s.id}`),
        api.get(`/users/students/${s.id}/active-session`),
        api.get(`/users/students/${s.id}/security-logs?limit=10`),
      ]);
      const detail = r.status === "fulfilled" ? r.value.data : { student: s };
      setViewing({
        ...detail,
        session: sess.status === "fulfilled" ? sess.value.data : null,
        security_logs: logs.status === "fulfilled" ? logs.value.data : [],
      });
      setPickedCourseId(detail.student?.selected_course_id || "");
    } catch (e) {
      toast.error("Could not load student details");
    } finally {
      setViewLoading(false);
    }
  };

  const forceLogout = async () => {
    if (!viewing?.student) return;
    if (!window.confirm("Force this student to log out of their current device?")) return;
    try {
      await api.post(`/users/students/${viewing.student.id}/force-logout`);
      toast.success("Student has been logged out successfully.");
      const [sess, logs] = await Promise.all([
        api.get(`/users/students/${viewing.student.id}/active-session`),
        api.get(`/users/students/${viewing.student.id}/security-logs?limit=10`),
      ]);
      setViewing((v) => ({ ...v, session: sess.data, security_logs: logs.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to force logout");
    }
  };

  const saveCourse = async () => {
    if (!pickedCourseId || !viewing?.student) return;
    try {
      const r = await api.patch(`/users/${viewing.student.id}/course`, { selected_course_id: pickedCourseId });
      toast.success("Enrolled course updated");
      const d = await api.get(`/users/students/${viewing.student.id}`).catch(() => null);
      setViewing((v) => ({ ...v, student: d?.data?.student || { ...v.student, selected_course_id: pickedCourseId, selected_course_name: r.data.selected_course_name } }));
      setEditingCourse(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    const { kind, student } = confirm;
    try {
      if (kind === "deactivate") {
        const r = await api.post(`/users/${student.id}/deactivate`);
        toast.success(r.data?.message || "Deactivated");
      } else if (kind === "activate") {
        const r = await api.post(`/users/${student.id}/activate`);
        toast.success(r.data?.message || "Activated");
      } else if (kind === "delete") {
        const r = await api.delete(`/users/${student.id}`);
        toast.success(r.data?.message || "Deleted");
      }
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const filtered = items;

  const showDeactivate = (s) => canDeactivate && (s.status === "approved" || s.status === "active");
  const showActivate = (s) => canActivate && s.status === "inactive";

  const ActionButtons = ({ s }) => (
    <div className="hidden lg:flex items-center justify-end gap-1">
      {canView && (
        <Button size="icon" variant="outline" onClick={() => openView(s)} title="View" data-testid={`view-${s.id}`}>
          <Eye className="w-4 h-4" />
        </Button>
      )}
      {showDeactivate(s) && (
        <Button size="icon" variant="outline" className="text-amber-700 hover:text-amber-800" onClick={() => setConfirm({ kind: "deactivate", student: s })} title="Deactivate" data-testid={`deactivate-${s.id}`}>
          <Ban className="w-4 h-4" />
        </Button>
      )}
      {showActivate(s) && (
        <Button size="icon" variant="outline" className="text-green-700 hover:text-green-800" onClick={() => setConfirm({ kind: "activate", student: s })} title="Activate" data-testid={`activate-${s.id}`}>
          <Power className="w-4 h-4" />
        </Button>
      )}
      {canDelete && (
        <Button size="icon" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={() => setConfirm({ kind: "delete", student: s })} title="Delete" data-testid={`delete-${s.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );

  const ActionMenu = ({ s }) => (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="outline" data-testid={`menu-${s.id}`}><MoreVertical className="w-4 h-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {canView && <DropdownMenuItem onClick={() => openView(s)}><Eye className="w-4 h-4 mr-2" />View</DropdownMenuItem>}
          {isSuper && s.role === "student" && s.status !== "deleted" && (
            <DropdownMenuItem onClick={() => setManageEnrollmentsFor(s)} data-testid={`manage-enrollments-${s.id}`}>
              <ShieldAlert className="w-4 h-4 mr-2" />Manage Enrollments
            </DropdownMenuItem>
          )}
          {showDeactivate(s) && <DropdownMenuItem onClick={() => setConfirm({ kind: "deactivate", student: s })}><Ban className="w-4 h-4 mr-2" />Deactivate</DropdownMenuItem>}
          {showActivate(s) && <DropdownMenuItem onClick={() => setConfirm({ kind: "activate", student: s })}><Power className="w-4 h-4 mr-2" />Activate</DropdownMenuItem>}
          {canDelete && <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600 focus:text-red-700" onClick={() => setConfirm({ kind: "delete", student: s })}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
          </>}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const confirmMessages = {
    deactivate: {
      title: "Deactivate student?",
      desc: "Are you sure you want to deactivate this student? They will no longer be able to access review content.",
      cta: "Deactivate",
      ctaClass: "bg-amber-600 hover:bg-amber-700",
    },
    activate: {
      title: "Activate student?",
      desc: "Are you sure you want to activate this student? They will regain access to the platform.",
      cta: "Activate",
      ctaClass: "bg-green-600 hover:bg-green-700",
    },
    delete: {
      title: "Permanently delete this student?",
      desc: "This action cannot be undone. If the student has review history, the account will be archived (soft-deleted) instead.",
      cta: "Delete",
      ctaClass: "bg-red-600 hover:bg-red-700",
    },
  };
  const cm = confirm ? confirmMessages[confirm.kind] : null;

  return (
    <div className="space-y-6" data-testid="student-list-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Students</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Student List</h1>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="search-students" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden grid gap-3">
        {filtered.length === 0 ? <Card className="p-8 text-center text-slate-500">No students</Card> :
          filtered.map((u) => (
            <Card key={u.id} className="p-4 border-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{u.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Badge className={`${statusColors[u.status] || "bg-slate-100"} capitalize`}>{u.status}</Badge>
                    <EnrolledCourseBadges u={u} />
                  </div>
                  {u.school && <div className="text-xs text-slate-500 mt-1">{u.school}</div>}
                </div>
                <ActionMenu s={u} />
              </div>
              {canEdit && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-xs text-slate-500 mb-1">Set status</div>
                  <Select value={u.status} onValueChange={(v) => setStatus(u.id, v)}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </Card>
          ))}
      </div>

      {/* Desktop table */}
      <Card className="p-0 overflow-hidden border-slate-200 hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>School</TableHead>
              <TableHead>Enrolled Courses</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Login</TableHead>
              {canEdit && <TableHead>Set Status</TableHead>}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={canEdit ? 8 : 7} className="text-center text-slate-500 py-8">No students</TableCell></TableRow>
            ) : filtered.map((u) => (
              <TableRow key={u.id} className="even:bg-slate-50">
                <TableCell className="font-medium">{u.full_name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.school || "—"}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1 max-w-xs"><EnrolledCourseBadges u={u} /></div></TableCell>
                <TableCell><Badge className={`${statusColors[u.status] || "bg-slate-100"} capitalize`}>{u.status}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">{u.last_login?.slice(0, 16).replace("T", " ") || "—"}</TableCell>
                {canEdit && (
                  <TableCell>
                    <Select value={u.status} onValueChange={(v) => setStatus(u.id, v)}>
                      <SelectTrigger className="w-32 h-9" data-testid={`set-status-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                )}
                <TableCell><div className="flex items-center justify-end gap-1"><ActionButtons s={u} /><ActionMenu s={u} /></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Pager page={page} pages={meta.pages} total={meta.total} count={items.length} onPage={setPage} />

      {/* View Student Profile Modal */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Student Profile</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-display text-lg font-bold">
                  {(viewing.student.full_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div className="font-display font-bold text-xl">{viewing.student.full_name}</div>
                  <div className="text-sm text-slate-500">{viewing.student.email}</div>
                </div>
                <Badge className={`${statusColors[viewing.student.status] || "bg-slate-100"} capitalize ml-auto`}>{viewing.student.status}</Badge>
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><div className="text-xs text-slate-500">Contact Number</div><div className="font-medium">{viewing.student.contact_number || "—"}</div></div>
                <div><div className="text-xs text-slate-500">School</div><div className="font-medium">{viewing.student.school || "—"}</div></div>
                <div className="sm:col-span-2">
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    Enrolled Courses
                    {isSuper && !editingCourse && (
                      <button onClick={() => setEditingCourse(true)} className="text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="edit-course-btn">
                        <Pencil className="w-3 h-3" /> Change primary
                      </button>
                    )}
                  </div>
                  {!editingCourse ? (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid="student-detail-enrolled-courses"><EnrolledCourseBadges u={viewing.student} /></div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <Select value={pickedCourseId} onValueChange={setPickedCourseId}>
                        <SelectTrigger className="h-9 flex-1" data-testid="course-select"><SelectValue placeholder="Pick a course" /></SelectTrigger>
                        <SelectContent>
                          {coursesList.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={saveCourse} data-testid="save-course-btn">Save</Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditingCourse(false); setPickedCourseId(viewing.student.selected_course_id || ""); }}>Cancel</Button>
                    </div>
                  )}
                </div>
                <div><div className="text-xs text-slate-500">Address</div><div className="font-medium">{viewing.student.address || "—"}</div></div>
                <div><div className="text-xs text-slate-500">Year Level</div><div className="font-medium">{viewing.student.year_level || "—"}</div></div>
                <div className="sm:col-span-2"><div className="text-xs text-slate-500">Board Exam Category</div><div className="font-medium">{viewing.student.board_exam_category || "—"}</div></div>
                <div><div className="text-xs text-slate-500">Registered</div><div className="font-medium">{viewing.student.created_at?.slice(0, 10) || "—"}</div></div>
                <div><div className="text-xs text-slate-500">Last Login</div><div className="font-medium">{viewing.student.last_login?.slice(0, 16).replace("T", " ") || "—"}</div></div>
                <div data-testid="student-registration-method">
                  <div className="text-xs text-slate-500">Registration Method</div>
                  <div className="font-medium capitalize">
                    {(viewing.student.auth_provider || "email").replace("_linked", " (linked)").replace("google", "Google").replace("apple", "Apple")}
                  </div>
                </div>
                <div data-testid="student-email-verified">
                  <div className="text-xs text-slate-500">Email Verified</div>
                  <div className="font-medium">{viewing.student.email_verified ? "Yes" : "No"}</div>
                </div>
                <div className="sm:col-span-2" data-testid="student-profile-completion">
                  <div className="text-xs text-slate-500">Profile Completion</div>
                  <div className="font-medium flex items-center gap-2">
                    {viewing.student.profile_completion_status === "complete" ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Complete</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Incomplete</Badge>
                    )}
                    {(() => {
                      const missing = [];
                      if (!viewing.student.full_name) missing.push("Full Name");
                      if (!viewing.student.contact_number) missing.push("Contact Number");
                      if (!viewing.student.school) missing.push("School");
                      if (!viewing.student.selected_course_id) missing.push("Selected Course");
                      return missing.length > 0 ? (
                        <span className="text-xs text-slate-500">Missing: {missing.join(", ")}</span>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>

              {viewing.session && (
                <div data-testid="active-session-section">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Active Device Session</div>
                  <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${viewing.session.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                          <span className="text-sm font-semibold">{viewing.session.is_active ? "Logged in" : "Not logged in"}</span>
                        </div>
                        {viewing.session.is_active && viewing.session.device_info && (
                          <>
                            <div className="text-sm text-slate-700">{viewing.session.device_info.device_name || "Unknown device"}</div>
                            <div className="text-[11px] text-slate-500">
                              {viewing.session.device_info.browser} · {viewing.session.device_info.operating_system}
                              {viewing.session.device_info.ip_address ? ` · ${viewing.session.device_info.ip_address}` : ""}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              Login time: {viewing.session.current_login_at?.slice(0, 16).replace("T", " ") || "—"}
                            </div>
                          </>
                        )}
                      </div>
                      {viewing.session.is_active && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={forceLogout}
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                          data-testid="force-logout-btn"
                        >
                          Force Logout Student
                        </Button>
                      )}
                    </div>
                  </div>
                  {viewing.security_logs && viewing.security_logs.length > 0 && (
                    <details className="mt-3" data-testid="security-logs-details">
                      <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-700">
                        Recent security events ({viewing.security_logs.length})
                      </summary>
                      <ul className="mt-2 space-y-1 text-[11px] text-slate-600 border border-slate-200 rounded-md p-2 max-h-48 overflow-y-auto">
                        {viewing.security_logs.map((l) => (
                          <li key={l.id} className="flex items-center justify-between gap-2 py-0.5">
                            <span className="font-medium">{l.event_type.replace(/_/g, " ")}</span>
                            <span className="text-slate-400">{l.device_name?.slice(0, 30)} · {l.event_time?.slice(0, 16).replace("T", " ")}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {viewing.progress && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Progress Summary</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "Answered", value: viewing.progress.total_answered, icon: GraduationCap, color: "text-slate-700" },
                      { label: "Correct", value: viewing.progress.correct, icon: CheckCircle2, color: "text-green-700" },
                      { label: "Incorrect", value: viewing.progress.incorrect, icon: XCircle, color: "text-red-600" },
                      { label: "Score", value: `${viewing.progress.score_percentage}%`, icon: GraduationCap, color: "text-blue-700" },
                    ].map((s) => (
                      <div key={s.label} className="border border-slate-200 rounded-md p-3">
                        <div className={`text-2xl font-display font-bold ${s.color}`}>{s.value}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-slate-500 mt-2">Modules attempted: <span className="font-semibold text-slate-700">{viewing.progress.modules_attempted}</span></div>
                </div>
              )}

              {viewing.recent_activity && viewing.recent_activity.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Recent Activity</div>
                  <div className="border border-slate-200 rounded-md divide-y max-h-72 overflow-y-auto">
                    {viewing.recent_activity.map((a, i) => (
                      <div key={a.id || a.answered_at || `activity-${i}`} className="p-3 text-sm flex items-start gap-3">
                        {a.is_correct
                          ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          : <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-slate-800 line-clamp-2">{a.question_text}</div>
                          <div className="text-xs text-slate-500 mt-1">
                            <span>{a.module_title}</span>
                            <span className="mx-2">•</span>
                            <span>Answered {a.selected_answer}</span>
                            <span className="mx-2">•</span>
                            <span>{a.answered_at?.slice(0, 16).replace("T", " ")}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewing.recent_activity && viewing.recent_activity.length === 0 && !viewLoading && (
                <div className="text-sm text-slate-500 text-center py-4 border border-slate-200 rounded-md">No review activity yet.</div>
              )}

              {viewLoading && <div className="text-sm text-slate-500 text-center">Loading details…</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{cm?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {cm?.desc}
              {confirm?.student && (
                <span className="block mt-2 font-medium text-slate-900">{confirm.student.full_name} — {confirm.student.email}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed} className={cm?.ctaClass} data-testid="confirm-action">{cm?.cta}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ManageEnrollmentsModal
        open={!!manageEnrollmentsFor}
        studentId={manageEnrollmentsFor?.id}
        studentName={manageEnrollmentsFor?.full_name}
        onClose={() => setManageEnrollmentsFor(null)}
      />
    </div>
  );
}

function EnrolledCourseBadges({ u }) {
  const list = u.enrolled_courses?.length
    ? u.enrolled_courses
    : (u.selected_course_name ? [{ id: u.selected_course_id, course_name: u.selected_course_name, is_primary: true }] : []);
  if (!list.length) return <span className="text-slate-400">—</span>;
  return list.map((c) => (
    <Badge key={c.id} variant="outline" className={`font-normal ${c.is_primary ? "border-blue-300 text-blue-800 dark:text-blue-200" : ""}`} data-testid={`student-course-badge-${u.id}-${c.id}`}>
      <GraduationCap className="w-3 h-3 mr-1 shrink-0" />{c.course_name}{c.is_primary && list.length > 1 ? " (Primary)" : ""}
    </Badge>
  ));
}
